import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BarChart3, Palette, Zap, Link as LinkIcon, FileText, Video, Music, Contact, Wifi, ArrowRight, RefreshCw } from "lucide-react";
import Marquee from "react-fast-marquee";

const types = [
  { icon: LinkIcon, label: "URL" },
  { icon: FileText, label: "PDF" },
  { icon: Video, label: "Video" },
  { icon: Music, label: "Audio" },
  { icon: Contact, label: "vCard" },
  { icon: Wifi, label: "WiFi" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="EXPLORE ME" className="h-14 w-auto" />
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#types" className="hover:text-foreground transition-colors">Content Types</a>
            <a href="#how" className="hover:text-foreground transition-colors">How it works</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-login-btn">Sign in</Button></Link>
            <Link to="/register"><Button size="sm" data-testid="nav-register-btn">Get started</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-card text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-6">
              <RefreshCw className="h-3 w-3 text-primary" /> Dynamic QR Codes
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-none mb-6">
              One QR code.<br />
              <span className="text-primary">Infinite destinations.</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed mb-8">
              Generate dynamic QR codes for URLs, PDFs, video, audio, WiFi and more.
              Change the destination anytime, customize the design, and track every single scan.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/register">
                <Button size="lg" data-testid="hero-cta-btn" className="group">
                  Start generating free
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/login"><Button size="lg" variant="outline">Sign in</Button></Link>
            </div>
          </div>
          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-2xl border border-border bg-card p-6 shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxhbmFseXRpY3MlMjBkYXNoYm9hcmQlMjBzY3JlZW58ZW58MHx8fHwxNzg1MzEyNDEyfDA&ixlib=rb-4.1.0&q=85"
                alt="Analytics dashboard preview"
                className="rounded-lg w-full h-auto object-cover"
              />
              <div className="absolute -bottom-4 -left-4 bg-background border border-border rounded-xl px-4 py-3 shadow-lg flex items-center gap-3">
                <BarChart3 className="h-5 w-5 text-success" />
                <div>
                  <p className="text-xs text-muted-foreground">Total Scans</p>
                  <p className="font-display font-bold text-lg">24,891</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee of types */}
        <div id="types" className="border-y border-border bg-card py-6">
          <Marquee gradient={false} speed={40}>
            {[...types, ...types].map((t, i) => (
              <div key={i} className="flex items-center gap-2 mx-8 text-muted-foreground">
                <t.icon className="h-5 w-5" />
                <span className="font-display font-medium">{t.label}</span>
              </div>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-3">Why EXPLORE ME</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Everything you need to run smart QR campaigns.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          <FeatureCard className="md:col-span-4" icon={RefreshCw} title="Editable destinations"
            desc="Update where your QR code points without ever reprinting it. Perfect for menus, packaging and posters." />
          <FeatureCard className="md:col-span-2" icon={Palette} title="Full design control"
            desc="Colors, dot styles, corner shapes and your own logo." />
          <FeatureCard className="md:col-span-2" icon={BarChart3} title="Deep analytics"
            desc="Scans over time, geolocation, device, browser and OS." />
          <FeatureCard className="md:col-span-4" icon={Zap} title="Any content type"
            desc="URLs, PDFs, MP3/MP4/AVI files, WiFi, vCard, email, SMS and plain text — upload files or link existing ones." />
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-3 gap-8">
          {[
            { n: "01", t: "Create & customize", d: "Pick a content type, add your data and style the QR to match your brand." },
            { n: "02", t: "Download & share", d: "Export as PNG or SVG and place it anywhere — print or digital." },
            { n: "03", t: "Track & optimize", d: "Watch scans roll in with rich analytics and update destinations anytime." },
          ].map((s) => (
            <div key={s.n} className="border-l-2 border-primary pl-6">
              <p className="font-display text-4xl font-bold text-primary/20 mb-3">{s.n}</p>
              <h3 className="font-display text-xl font-semibold mb-2">{s.t}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="rounded-2xl bg-primary text-primary-foreground p-12 md:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-10" />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Ready to go dynamic?</h2>
            <p className="text-primary-foreground/80 mb-8 max-w-lg mx-auto">Create your first dynamic QR code in under a minute. No credit card required.</p>
            <Link to="/register">
              <Button size="lg" variant="secondary" data-testid="cta-bottom-btn">
                Get started free <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="EXPLORE ME" className="h-8 w-auto" />
          </div>
          <p>© 2026 EXPLORE ME. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc, className = "" }) {
  return (
    <div className={`group border border-border rounded-xl bg-card p-8 hover:-translate-y-1 hover:shadow-sm transition-transform duration-200 ${className}`}>
      <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
        <Icon className="h-5 w-5 text-primary group-hover:text-primary-foreground" />
      </div>
      <h3 className="font-display text-xl font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{desc}</p>
    </div>
  );
}
